import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Upload, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";

const StarPick = ({ v, set }) => (
  <div className="flex gap-1" role="radiogroup" aria-label="Star rating" data-testid="review-stars">
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" role="radio" aria-checked={v === i} aria-label={`${i} star${i > 1 ? "s" : ""}`} onClick={() => set(i)} className="transition-transform hover:scale-110" data-testid={`review-star-${i}`}>
        <Star className="h-9 w-9" style={i <= v ? { color: "var(--brand-accent)", fill: "var(--brand-accent)" } : { color: "#cbd5e1" }} />
      </button>))}
  </div>
);

const PhotoPick = ({ token, v, set }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f); setBusy(true);
    try { const { data } = await api.post(`/public/review/${token}/photo`, fd); set(data.url); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-4">
      {v ? <img src={v} alt="Your photo" className="h-16 w-16 rounded-full object-cover" data-testid="review-photo-preview" /> : <span className="h-16 w-16 rounded-full bg-slate-100" />}
      <button type="button" className="btn-outline btn-sm" onClick={() => ref.current.click()} disabled={busy} data-testid="review-photo-upload"><Upload className="h-3 w-3" />{busy ? "Uploading…" : v ? "Change photo" : "Add a photo"}</button>
      {v && <button type="button" className="btn-ghost btn-sm" onClick={() => set("")}>Remove</button>}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="review-photo-file" />
    </div>
  );
};

export default function ReviewPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ rating: 0, quote: "", display_name: "", trip: "", photo: "", consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { api.get(`/public/review/${token}`).then((r) => { setInfo(r.data); setF((x) => ({ ...x, display_name: r.data.name, trip: r.data.tour_title })); }).catch((e) => setErr(errMsg(e))); }, [token]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) { toast.error("Please choose a star rating"); return; }
    setBusy(true);
    try { await api.post(`/public/review/${token}`, f); setDone(true); } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  if (err) return <p className="container-x py-24 text-center text-slate-600" data-testid="review-error">{err}</p>;
  if (!info) return <p className="py-24 text-center text-slate-500">Loading…</p>;
  if (done || info.submitted) return (
    <div className="container-x max-w-xl py-24 text-center" data-testid="review-thanks"><CheckCircle2 className="mx-auto h-12 w-12" style={{ color: "var(--brand)" }} />
      <h1 className="mt-4 text-4xl font-semibold">Thank you{info.first_name ? `, ${info.first_name}` : ""}!</h1><p className="mt-3 text-slate-600">We've received your review of {info.tour_title}. Our team reads every review before it's shared on our website.</p></div>
  );
  return (
    <div className="container-x max-w-2xl py-16" data-testid="review-page">
      <p className="eyebrow">Your review</p><h1 className="mt-2 text-4xl font-semibold sm:text-5xl">How was {info.tour_title}?</h1>
      <p className="mt-3 text-slate-600">Hi {info.first_name}, we'd love to hear about your trip. It only takes a couple of minutes.</p>
      <form className="card mt-8 space-y-5 p-6" onSubmit={submit} data-testid="review-form">
        <div><label className="label">Your rating</label><StarPick v={f.rating} set={(v) => set("rating", v)} /></div>
        <div><label className="label" htmlFor="rv-quote">Your review</label><textarea id="rv-quote" className="input" rows={6} required minLength={10} maxLength={1500} value={f.quote} onChange={(e) => set("quote", e.target.value)} placeholder="What made the trip special? Any favourite moments?" data-testid="review-quote" /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className="label" htmlFor="rv-name">Name to show with your review</label><input id="rv-name" className="input" required maxLength={60} value={f.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="review-name" /><p className="mt-1 text-xs text-slate-500">For example "Maria S." if you prefer.</p></div>
          <div><label className="label" htmlFor="rv-trip">Trip</label><input id="rv-trip" className="input" maxLength={80} value={f.trip} onChange={(e) => set("trip", e.target.value)} data-testid="review-trip" /></div>
        </div>
        <div><label className="label">Photo (optional)</label><PhotoPick token={token} v={f.photo} set={(v) => set("photo", v)} /></div>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} required data-testid="review-consent" />A2Z Custom Travel may share my review, name and photo on its website.</label>
        <button className="btn-primary" disabled={busy} data-testid="review-submit">{busy ? "Sending…" : "Send my review"}</button>
      </form>
    </div>
  );
}
